import { AbstractFWGlobalParams } from 'tag/ts/tag/ads/freewheel/abstract/AbstractFWGlobalParams';
import { IFWGlobalDAIAPIParams } from 'tag/ts/tag/interfaces/ads/freewheel/IFWGlobalDAIAPIParams';
import { IFWGlobalDAIParams } from 'tag/ts/tag/interfaces/ads/freewheel/IFWGlobalDAIParams';
import { IFWGlobalIMAParams } from 'tag/ts/tag/interfaces/ads/freewheel/IFWGlobalIMAParams';
import { IFWGlobalLinearParams } from 'tag/ts/tag/interfaces/ads/freewheel/IFWGlobalLinearParams';
import { IFWGlobalParamsArgument } from 'tag/ts/tag/interfaces/arguments/IFWGlobalParamsArgument';
import { AdSetupDefinition } from 'tag/ts/tag/model/type/ads/AdSetupDefinition';
import { TFWGlobalParams } from 'tag/ts/tag/types/ads/freewheel/TFWGlobalParams';

/**
 * @copyright © Paramount Streaming
 * @description Freewheel global parameters for streaming ads
 */
export class FWGlobalParams extends AbstractFWGlobalParams {

	/**
	 * @description Internal store for global ad parameters
	 * @readonly
	 */
	protected readonly adParams: TFWGlobalParams;

	constructor( argument: IFWGlobalParamsArgument ) {
		super(
			{
				name: 'FWGlobalParams',
				...argument
			}
		);

		switch ( argument?.adSetupInfo?.setup?.definition ) {
			case AdSetupDefinition.IMA:
				this.adParams = this.createIMAParams();
				this?.logger?.log( 'AdSetupDefinition = IMA, createIMAParams', 'params:', this.adParams );
				break;
			case AdSetupDefinition.DAI_SDK:
			case AdSetupDefinition.DAI_SSB:
				this.adParams = this.createDAIParams();
				this?.logger?.log( 'AdSetupDefinition = DAI_SDK | DAI_SSB, createDAIParams', 'params:', this.adParams );
				break;
			case AdSetupDefinition.DAI_API:
				this.adParams = this.createDAIAPIParams();
				this?.logger?.log( 'AdSetupDefinition = DAI_API, createDAIAPIParams', 'params:', this.adParams );
				break;
			case AdSetupDefinition.LINEAR_AD_TRACKING:
				this.adParams = this.createLinearParams();
				break;
		}
	}

	/**
	 * @description Returns global parameters for streaming ads
	 * @returns {TFWGlobalParams}
	 */
	get adParameters(): TFWGlobalParams {
		return this.adParams;
	}

	protected createIMAParams(): IFWGlobalIMAParams {
		return {
			asnw: this.fwConstants.assetNetworkId,
			caid: this.apiMetadata?.contentId,
			csid: this.siteSessionData?.csid?.toLowerCase() || this.createSiteSectionId(),
			flag: this.fwConstants.capabilitiesFlag,
			metr: this.fwConstants.defaultCapabilities,
			nw: this.fwConstants.networkId,
			prof: this.fwConstants.playerProfile,
			pvrn: this.fwConstants.pageView,
			resp: this.fwConstants.response,
			ssnw: this.fwConstants.siteSectionNetworkId,
			vprn: this.fwConstants.videoPlayer
		};
	}

	protected createDAIParams(): IFWGlobalDAIParams {
		return {
			imafw_csid: this.siteSessionData?.csid?.toLowerCase() || this.createSiteSectionId()
		};
	}

	protected createLinearParams(): IFWGlobalLinearParams {
		return {
			asnw: this.fwConstants.assetNetworkId,
			caid: '[CAID]',
			csid: this.createSiteSectionId(),
			flag: '+amcb+emcr+dtrd+sltp+slcb+exvt+fbad+sync+nucr+aeti',
			metr: this.fwConstants.defaultCapabilities,
			mode: 'LIVE',
			nw: this.fwConstants.assetNetworkId,
			prof: `${this.fwConstants.assetNetworkId}:hylda`,
			pvrn: this.fwConstants.randomTag,
			resp: this.fwConstants.response,
			ssnw: this.fwConstants.assetNetworkId,
			vdty: 'VARIABLE',
			vrdu: this.fwConstants.durTag,
			vprn: this.fwConstants.randomTag
		};
	}

	protected createDAIAPIParams(): IFWGlobalDAIAPIParams {
		return {
			imafw_csid: 'streaming_paramountplus_ctv_unknown_vod',
			imafw_cpPre: !!this.isLive() ? undefined : '0',
			imafw_cpSession: !!this.isLive() ? undefined : '0',
			imafw_description_url: 'https%3A%2F%2Fwww.paramountplus.com%2F'
		};
	}
}